import React from 'react';
import { Card, Typography } from 'antd';
import { RobotOutlined, UserOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatMessage as ChatMessageType } from '../../api/aiChatApi';

const { Text, Paragraph } = Typography;

interface ChatMessageProps {
  message: ChatMessageType;
  isStreaming?: boolean;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, isStreaming }) => {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`flex ${isUser ? 'flex-row-reverse' : 'flex-row'} items-start max-w-3xl`}>
        <div className={`flex-shrink-0 ${isUser ? 'ml-3' : 'mr-3'}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isUser ? 'bg-blue-500' : 'bg-purple-500'
          }`}>
            {isUser ? <UserOutlined className="text-white" /> : <RobotOutlined className="text-white" />}
          </div>
        </div>
        
        <div className="flex-1">
          <Card 
            className={`${isUser ? 'bg-blue-50' : 'bg-white'} shadow-md`}
            bodyStyle={{ padding: '12px 16px' }}
          >
            <div className="mb-2">
              <Text strong className={isUser ? 'text-blue-700' : 'text-purple-700'}>
                {isUser ? 'You' : 'AI Assistant'}
              </Text>
              <Text className="ml-3 text-xs text-gray-500">
                {new Date(message.timestamp).toLocaleTimeString()}
              </Text>
            </div>

            <div className="prose prose-sm max-w-none">
              {isUser ? (
                <Paragraph className="mb-0">{message.content}</Paragraph>
              ) : (
                <div className="markdown-content">
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      table: ({ children, ...props }) => (
                        <div className="overflow-x-auto my-4">
                          <table className="min-w-full border border-gray-300 rounded-lg" {...props}>
                            {children}
                          </table>
                        </div>
                      ),
                      thead: ({ children, ...props }) => (
                        <thead className="bg-gray-50" {...props}>
                          {children}
                        </thead>
                      ),
                      th: ({ children, ...props }) => (
                        <th className="px-4 py-2 text-left font-semibold text-gray-700 border-b border-gray-300" {...props}>
                          {children}
                        </th>
                      ),
                      td: ({ children, ...props }) => (
                        <td className="px-4 py-2 border-b border-gray-200" {...props}>
                          {children}
                        </td>
                      ),
                      p: ({ children, ...props }) => (
                        <p className="mb-3 last:mb-0" {...props}>
                          {children}
                        </p>
                      ),
                      strong: ({ children, ...props }) => (
                        <strong className="font-semibold text-gray-900" {...props}>
                          {children}
                        </strong>
                      ),
                      code: ({ children, ...props }) => (
                        <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono" {...props}>
                          {children}
                        </code>
                      ),
                      pre: ({ children, ...props }) => (
                        <pre className="bg-gray-100 p-3 rounded-lg overflow-x-auto my-3" {...props}>
                          {children}
                        </pre>
                      ),
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                  {isStreaming && (
                    <span aria-live="polite" className="inline-flex items-center ml-2 align-middle" title="AI is typing...">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce ml-1" style={{ animationDelay: '0.12s' }} />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce ml-1" style={{ animationDelay: '0.24s' }} />
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Data/metadata intentionally hidden by default. Use includeMeta=true to re-enable. */}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;

